import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Checkroom as CheckroomIcon,
  Event as EventIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { format } from 'date-fns';
import { calendarAPI, outfitsAPI } from '../services/api';
import { useUI } from '../contexts/UIContext';
import { CalendarEvent, DailyOutfit, Outfit } from '../types';
import GoogleCalendarSync from '../components/GoogleCalendarSync';

const Calendar: React.FC = () => {
  const { toast, confirm } = useUI();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [dailyOutfits, setDailyOutfits] = useState<DailyOutfit[]>([]);
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [outfitDialogOpen, setOutfitDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  
  // Form state
  const [eventName, setEventName] = useState('');
  const [eventType, setEventType] = useState('casual');
  const [eventTime, setEventTime] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventNotes, setEventNotes] = useState('');
  const [selectedOutfitId, setSelectedOutfitId] = useState<number | null>(null);
  const [useDefaultOutfit, setUseDefaultOutfit] = useState(false);
  const [defaultOutfitId, setDefaultOutfitId] = useState<number | null>(null);
  const [defaultOutfitType, setDefaultOutfitType] = useState('casual');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, dailyRes, outfitsRes] = await Promise.all([
        calendarAPI.getEvents(),
        calendarAPI.getDailyOutfits(),
        outfitsAPI.getOutfits(),
      ]);
      
      setEvents(eventsRes.data);
      setDailyOutfits(dailyRes.data);
      setOutfits(outfitsRes.data);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  };

  const handleDateClick = (arg: any) => {
    setSelectedDate(new Date(arg.date));
    setEventDialogOpen(true);
    resetEventForm();
  };

  const handleEventClick = (arg: any) => {
    const eventId = parseInt(arg.event.id);
    const event = events.find(e => e.id === eventId);
    if (event) {
      setSelectedEvent(event);
      setEventName(event.event_name);
      setEventType(event.event_type || 'casual');
      setEventTime(event.time || '');
      setEventLocation(event.location || '');
      setEventNotes(event.notes || '');
      setSelectedOutfitId(event.outfit_id || null);
      setUseDefaultOutfit(event.use_default_outfit);
      setEventDialogOpen(true);
    }
  };

  const renderOutfitAnalysis = (event: CalendarEvent) => {
    if (!event.outfit_analysis) return null;

    const analysis = event.outfit_analysis;

    return (
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">AI Outfit Analysis</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              {analysis.requires_outfit ? (
                <Chip icon={<CheckroomIcon />} label="Outfit Required" color="primary" size="small" />
              ) : (
                <Chip icon={<CheckCircleIcon />} label="No Outfit Needed" color="default" size="small" />
              )}
              <Chip label={`Importance: ${analysis.importance}/10`} size="small" variant="outlined" />
            </Box>

            {analysis.requires_outfit && (
              <>
                <Box>
                  <Typography variant="caption" color="textSecondary">Formality:</Typography>
                  <Typography variant="body2">{analysis.formality_level}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Occasion:</Typography>
                  <Typography variant="body2">{analysis.occasion}</Typography>
                </Box>
                {analysis.style_suggestions && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">Style Suggestions:</Typography>
                    <Typography variant="body2">{analysis.style_suggestions}</Typography>
                  </Box>
                )}
                {analysis.reasoning && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">AI Reasoning:</Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      {analysis.reasoning}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const handleSaveEvent = async () => {
    if (!selectedDate) return;

    try {
      const eventData = {
        date: format(selectedDate, 'yyyy-MM-dd'),
        event_name: eventName,
        event_type: eventType,
        time: eventTime || null,
        location: eventLocation,
        notes: eventNotes,
        outfit_id: selectedOutfitId,
        use_default_outfit: useDefaultOutfit,
      };

      if (selectedEvent) {
        await calendarAPI.updateEvent(selectedEvent.id, eventData);
      } else {
        await calendarAPI.createEvent(eventData);
      }

      fetchData();
      setEventDialogOpen(false);
      resetEventForm();
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    const ok = await confirm({
      title: 'Delete this event?',
      message: 'It will be removed from your calendar.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await calendarAPI.deleteEvent(selectedEvent.id);
      fetchData();
      setEventDialogOpen(false);
      resetEventForm();
      toast('Event deleted', 'success');
    } catch (error) {
      console.error('Error deleting event:', error);
      toast('Failed to delete event', 'error');
    }
  };

  const handleSetDailyOutfit = async () => {
    if (!selectedDate) return;

    try {
      await calendarAPI.setDailyOutfit({
        date: format(selectedDate, 'yyyy-MM-dd'),
        default_outfit_id: defaultOutfitId,
        default_outfit_type: defaultOutfitType,
      });

      fetchData();
      setOutfitDialogOpen(false);
    } catch (error) {
      console.error('Error setting daily outfit:', error);
    }
  };

  const resetEventForm = () => {
    setSelectedEvent(null);
    setEventName('');
    setEventType('casual');
    setEventTime('');
    setEventLocation('');
    setEventNotes('');
    setSelectedOutfitId(null);
    setUseDefaultOutfit(false);
  };

  // Convert events to FullCalendar format
  const calendarEvents = events.map(event => {
    let title = event.event_name;

    // Add indicators for outfit status
    if (event.requires_outfit === false) {
      title = `✓ ${title}`; // No outfit needed
    } else if (event.outfit_id) {
      title = `👔 ${title}`; // Outfit assigned
    } else if (event.requires_outfit === true) {
      title = `⚠️ ${title}`; // Needs outfit
    }

    // If synced from Google, add indicator
    if (event.synced_from_google) {
      title = `📅 ${title}`;
    }

    return {
      id: event.id.toString(),
      title,
      date: event.date,
      backgroundColor: getEventColor(event.event_type),
      borderColor: event.outfit_id ? '#4caf50' : (event.requires_outfit ? '#ff9800' : undefined),
      extendedProps: {
        type: event.event_type,
        outfit: event.outfit,
        location: event.location,
        requiresOutfit: event.requires_outfit,
        hasOutfit: !!event.outfit_id,
        syncedFromGoogle: event.synced_from_google,
      },
    };
  });

  // Add daily outfit indicators
  dailyOutfits.forEach(daily => {
    calendarEvents.push({
      id: `daily-${daily.id}`,
      title: '👔 Default Outfit',
      date: daily.date,
      backgroundColor: '#4caf50',
      borderColor: '#4caf50',
      extendedProps: {
        type: 'daily',
        outfit: daily.default_outfit,
        location: undefined,
        requiresOutfit: undefined,
        hasOutfit: !!daily.default_outfit,
        syncedFromGoogle: undefined,
      },
    });
  });

  function getEventColor(type: string | undefined): string {
    switch (type) {
      case 'formal': return '#9c27b0';
      case 'business': return '#2196f3';
      case 'casual': return '#4caf50';
      case 'party': return '#ff9800';
      case 'workout': return '#f44336';
      default: return '#607d8b';
    }
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Outfit Calendar
      </Typography>

      {/* Google Calendar Sync */}
      <GoogleCalendarSync onSyncComplete={fetchData} />

      {/* Legend */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Legend:</Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Chip icon={<CheckCircleIcon />} label="✓ No outfit needed" size="small" variant="outlined" />
          <Chip icon={<CheckroomIcon />} label="👔 Outfit assigned" size="small" color="success" variant="outlined" />
          <Chip icon={<WarningIcon />} label="⚠️ Needs outfit" size="small" color="warning" variant="outlined" />
          <Chip icon={<EventIcon />} label="📅 Google Calendar" size="small" color="info" variant="outlined" />
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <FullCalendar
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          events={calendarEvents}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          height="auto"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,dayGridWeek',
          }}
          eventDisplay="block"
        />
      </Paper>

      {/* Event Dialog */}
      <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedEvent ? 'Edit Event' : 'Add Event'} - {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Event Name"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              fullWidth
              required
            />
            
            <FormControl fullWidth>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={eventType}
                label="Event Type"
                onChange={(e) => setEventType(e.target.value)}
              >
                <MenuItem value="casual">Casual</MenuItem>
                <MenuItem value="business">Business</MenuItem>
                <MenuItem value="formal">Formal</MenuItem>
                <MenuItem value="party">Party</MenuItem>
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="workout">Workout</MenuItem>
                <MenuItem value="travel">Travel</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Time"
              type="time"
              value={eventTime}
              onChange={(e) => setEventTime(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label="Location"
              value={eventLocation}
              onChange={(e) => setEventLocation(e.target.value)}
              fullWidth
            />
            
            <TextField
              label="Notes"
              value={eventNotes}
              onChange={(e) => setEventNotes(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            
            <FormControl fullWidth>
              <InputLabel>Select Outfit</InputLabel>
              <Select
                value={selectedOutfitId || ''}
                label="Select Outfit"
                onChange={(e) => setSelectedOutfitId(e.target.value as number)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {outfits.map(outfit => (
                  <MenuItem key={outfit.id} value={outfit.id}>
                    {outfit.name} - {outfit.occasion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setOutfitDialogOpen(true)}
                startIcon={<CheckroomIcon />}
              >
                Set Default Outfit for Day
              </Button>
            </Box>

            {/* Show AI Outfit Analysis if available */}
            {selectedEvent && renderOutfitAnalysis(selectedEvent)}
          </Box>
        </DialogContent>
        <DialogActions>
          {selectedEvent && (
            <Button onClick={handleDeleteEvent} color="error">
              Delete
            </Button>
          )}
          <Button onClick={() => setEventDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEvent} variant="contained">
            {selectedEvent ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Daily Outfit Dialog */}
      <Dialog open={outfitDialogOpen} onClose={() => setOutfitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Set Default Outfit - {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Default Outfit Type</InputLabel>
              <Select
                value={defaultOutfitType}
                label="Default Outfit Type"
                onChange={(e) => setDefaultOutfitType(e.target.value)}
              >
                <MenuItem value="casual">Casual</MenuItem>
                <MenuItem value="business">Business</MenuItem>
                <MenuItem value="smart_casual">Smart Casual</MenuItem>
                <MenuItem value="athletic">Athletic</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Select Default Outfit</InputLabel>
              <Select
                value={defaultOutfitId || ''}
                label="Select Default Outfit"
                onChange={(e) => setDefaultOutfitId(e.target.value as number)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {outfits
                  .filter(o => o.style === defaultOutfitType || o.occasion === defaultOutfitType)
                  .map(outfit => (
                    <MenuItem key={outfit.id} value={outfit.id}>
                      {outfit.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            
            <Alert severity="info">
              This outfit will be the default for the entire day unless specific events override it.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutfitDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSetDailyOutfit} variant="contained">
            Set Default
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Calendar;